/**
 * `/restock potionshop` — a SERVER-side restock everyone sees.
 *
 * The Potion Shop's stock is a pure function of (cycle, salt), and the cycle
 * is the wall clock measured from an EPOCH. A restock moves the epoch to
 * "now" in `public.dev_settings` (key `potion_restock`), which every client
 * reads at boot and receives live over Realtime: the cycle index jumps, the
 * countdown restarts at a full ten minutes, the SOLD flags (keyed by cycle)
 * clear, and the restock watcher raises its banner exactly as it would for a
 * natural rollover. A player who logs in later reads the same epoch, so the
 * new stock is the same for everyone, not just those online at the moment.
 *
 * The epoch is kept device-local under `hambi.potionEpoch` (NOT a synced
 * save key — it is world state, not the player's progress), so the shop and
 * the watcher can read it synchronously.
 */
import type {
  SupabaseClient,
  RealtimePostgresInsertPayload,
  RealtimePostgresUpdatePayload,
} from '@supabase/supabase-js';

export const POTION_EPOCH_KEY = 'hambi.potionEpoch';
const SETTING_KEY = 'potion_restock';

interface SettingRow {
  key: string;
  value: { at?: unknown } | null;
}

/** The current epoch in epoch ms (0 when never restocked or unknown). */
export function potionEpoch(): number {
  try {
    const n = parseInt(window.localStorage.getItem(POTION_EPOCH_KEY) ?? '', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function storeEpoch(at: unknown): void {
  if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0) return;
  try {
    window.localStorage.setItem(POTION_EPOCH_KEY, String(Math.floor(at)));
  } catch {
    /* storage unavailable — the shop falls back to epoch 0 */
  }
}

/** Fetch the epoch once and follow it live. Inert without a client. */
export async function initPotionRestock(client: SupabaseClient): Promise<void> {
  try {
    const { data } = await client
      .from('dev_settings')
      .select('key,value')
      .eq('key', SETTING_KEY)
      .maybeSingle();
    const row = data as SettingRow | null;
    if (row?.value) storeEpoch(row.value.at);
  } catch (e) {
    console.warn('potion restock: read failed', e);
  }
  const apply = (msg: { new: SettingRow }) => {
    const row = msg.new;
    if (row?.key === SETTING_KEY && row.value) storeEpoch(row.value.at);
  };
  client
    .channel('dev-settings')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'dev_settings' },
      (msg: RealtimePostgresInsertPayload<SettingRow>) => apply(msg),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'dev_settings' },
      (msg: RealtimePostgresUpdatePayload<SettingRow>) => apply(msg),
    )
    .subscribe();
}
